import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Checkbox, Form, Input, Modal, Select } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { Store } from 'antd/es/form/interface';
import { useState } from 'react';

import type { Mode } from 'app/components/commons/Hotel/Types';
import { SearchBox } from 'app/components/fields/SearchBox';
import { ChannelManager as ChannelManagerType } from 'app/typings/channelManagers';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { AutoInventoryStatus } from 'app/utils/autoInventory/autoInventory';
import { ChannelManagerDataSource } from 'app/utils/channelManagers/channelManagers';
import { getDerbysoftSuppliersDataSource } from 'app/utils/channelManagers/derbysoft';
import { getRateModesDataSource } from 'app/utils/channelManagers/rateModes';

import { style } from '../Communication.style';

import { BarBBModal } from './BarBBModal';

type Props = {
  mode: Mode;
  values: Hotel;
  setFieldsValue: (value: Store) => void;
  isDisabled: boolean;
  hasChannelManager: boolean;
  openConfirmModal: boolean;
  setOpenConfirmModal: (open: boolean) => void;
};

const RULES = [{ required: true }];

export const ChannelManager = ({
  mode,
  values,
  setFieldsValue,
  isDisabled,
  hasChannelManager,
  openConfirmModal,
  setOpenConfirmModal,
}: Props) => {
  const [openBarBbModal, setOpenBarBbModal] = useState(false);
  const form = Form.useFormInstance<Hotel>();

  const handleBarBbChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked && !isDisabled) {
      setOpenBarBbModal(true);
      // Prevent the checkbox from being checked until user confirms
      setTimeout(() => {
        form.setFieldValue('connectedBarBb', false);
      }, 0);
    }
  };

  return (
    <>
      <Form.Item label="Channel manager" name="channelManager" rules={RULES}>
        <SearchBox
          dataSource={ChannelManagerDataSource}
          mode={mode}
          style={style.searchBoxField}
          disabled={isDisabled}
          onChange={(value: ChannelManagerType) =>
            values.stockScrapperEnabled &&
            value !== ChannelManagerType.NONE &&
            setOpenConfirmModal(true)
          }
        />
      </Form.Item>
      {hasChannelManager && (
        <Form.Item label="Connected rates" name="rateMode" rules={RULES}>
          <SearchBox
            dataSource={getRateModesDataSource(values.isLegacy)}
            mode={mode}
            style={style.searchBoxField}
            disabled={isDisabled}
          />
        </Form.Item>
      )}

      {hasChannelManager &&
        values.channelManager === ChannelManagerType.MEWS && (
          <Form.Item
            label="Channel Manager Credentials"
            name="channelManagerCredentials"
            rules={RULES}
          >
            <Input disabled={isDisabled} />
          </Form.Item>
        )}
      {hasChannelManager &&
        values.channelManager === ChannelManagerType.DERBYSOFT && (
          <Form.Item
            name="supplierId"
            label="Supplier"
            rules={[{ required: true }]}
          >
            <Select showSearch disabled={isDisabled}>
              {getDerbysoftSuppliersDataSource().map((supplier) => (
                <Select.Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      {!hasChannelManager && (
        <Form.Item label="Auto inventory" name="stockScrapperEnabled">
          <Select style={style.searchBoxField} disabled showArrow={false}>
            <Select.Option value={true}>
              {AutoInventoryStatus.Activated}
            </Select.Option>
            <Select.Option value={false}>
              {AutoInventoryStatus.Deactivated}
            </Select.Option>
          </Select>
        </Form.Item>
      )}
      <Form.Item
        label="Includes breakfast (BAR BB)"
        name="connectedBarBb"
        valuePropName="checked"
      >
        <Checkbox disabled={isDisabled} onChange={handleBarBbChange} />
      </Form.Item>
      <Modal
        key={'modal'}
        title={
          <>
            <ExclamationCircleOutlined style={{ color: '#FF382C' }} />
            <span style={{ marginLeft: '6px' }}>Warning</span>
          </>
        }
        open={openConfirmModal}
        onOk={() => {
          setOpenConfirmModal(false);
          setFieldsValue({
            ...values,
            stockScrapperEnabled: false,
          });
        }}
        onCancel={() => {
          setFieldsValue({
            ...values,
            channelManager: ChannelManagerType.NONE,
          });
          setOpenConfirmModal(false);
        }}
        okText="OK"
        cancelText="Cancel"
      >
        <p>This hotel has activated Auto inventory.</p>
        <p>
          Connecting to a Channel Manager will disable the feature and erase all
          connected data.
        </p>
      </Modal>
      <BarBBModal
        openBarBbModal={openBarBbModal}
        setOpenBarBbModal={setOpenBarBbModal}
        values={values}
        setFieldsValue={setFieldsValue}
      />
    </>
  );
};
